import ErrorDisplay from '../../components/ErrorDisplay';
import React from 'react';
import useAvailability from '../../operations/client/useAvailability';
import { Box, Button, Flex, Heading, IconButton, Progress, Spacer, Stack, Text, useBreakpointValue } from '@chakra-ui/react';
import { daysOrder, formatLocation, formatWeekday, locations, locationsFull } from '@opanuj/shared/utils';
import { EnrollmentLeadForm } from './EnrollmentLeadForm';
import { FaUndo } from 'react-icons/fa';
import { FiCalendar, FiChevronDown, FiChevronUp, FiClock, FiFilter, FiMapPin, FiUser } from 'react-icons/fi';
import { SlotCard } from './SlotCard';
import { SlotFilterSelect } from '../../components/SlotFilterSelect';

export const Availability = (props: any) => {
	const { isLoading, error, data } = useAvailability({ code: props.code, locations });
	const [filterTeachers, setFilterTeachers] = React.useState<string[]>([]);
	const [filterDays, setFilterDays] = React.useState<string[]>([]);
	const [filterDurations, setFilterDurations] = React.useState<string[]>([]);
	const [filterLocations, setFilterLocations] = React.useState<string[]>([]);
	const [mobileFiltersOpen, setMobileFiltersOpen] = React.useState(false);
	const isMobile = useBreakpointValue({ base: true, lg: false }) ?? false;

	const hasAnyFilters =
		filterTeachers.length > 0 ||
		filterDays.length > 0 ||
		filterDurations.length > 0 ||
		filterLocations.length > 0;

	const clearFilters = () => {
		setFilterTeachers([]);
		setFilterDays([]);
		setFilterDurations([]);
		setFilterLocations([]);
	};

	if (isLoading)
		return (
			<Flex direction="column" justifyContent="center" py={6}>
				<Progress.Root value={null} size="md" w="100%" colorPalette="orange" variant="subtle" shape="rounded">
					<Progress.Track>
						<Progress.Range />
					</Progress.Track>
				</Progress.Root>
			</Flex>
		);

	if (error) {
		console.error(error);
		return <ErrorDisplay />;
	}

	if (data.length) {
		const teacherOptions = Array.from(
			new Set(
				data.flatMap((item: any) => (item.teachers ?? []).filter(Boolean))
			)
		).sort((a, b) => a.localeCompare(b));
		const dayOptions = daysOrder.filter((day) =>
			data.some((item: any) => item.slot.day === day)
		);
		const durationOptions = Array.from(
			new Set(data.map((item: any) => String(item.slot.duration)))
		).sort((a, b) => Number(a) - Number(b));
		const locationOptions = Array.from(
			new Set(data.map((item: any) => item.slot.location))
		).sort((a, b) => a.localeCompare(b));

		const slots = [];
		for (let item of data) {
			for (let teacher of item.teachers) {
				let slot = item.slot;
				slot.dayFull = formatWeekday(slot.day);
				slot.locationFull = locationsFull[slot.location];
				if (filterTeachers.length > 0 && !filterTeachers.includes(slot.defaultTeacher)) continue;
				if (filterDays.length > 0 && !filterDays.includes(slot.day)) continue;
				if (filterDurations.length > 0 && !filterDurations.includes(String(slot.duration))) continue;
				if (filterLocations.length > 0 && !filterLocations.includes(slot.location)) continue;
				slots.push({ ...slot, teacher, capacity: item.capacity });
			}
		}
		slots.sort((l1: any, l2: any) => {
			if (daysOrder.indexOf(l1.day) - daysOrder.indexOf(l2.day))
				return daysOrder.indexOf(l1.day) - daysOrder.indexOf(l2.day);
			else return l1.time.localeCompare(l2.time);
		});

		const shouldShowScrollHint = slots.length > (isMobile ? 6 : 8);

		const renderFilterStack = (mobile = false) => (
			<Stack gap={3} direction={mobile ? 'column' : 'row'} p={mobile ? 0 : 3} flex="1" w="full">
				<SlotFilterSelect
					icon={FiCalendar}
					value={filterDays}
					placeholder="dzień tygodnia"
					onChange={setFilterDays}
					multiple
					options={dayOptions.map((day) => ({ value: day, label: formatWeekday(day) }))}
				/>
				<SlotFilterSelect
					icon={FiUser}
					value={filterTeachers}
					placeholder="korepetytor"
					onChange={setFilterTeachers}
					multiple
					options={teacherOptions.map((teacher) => ({ value: teacher, label: teacher }))}
				/>
				<SlotFilterSelect
					icon={FiClock}
					value={filterDurations}
					placeholder="długość zajęć"
					onChange={setFilterDurations}
					multiple
					options={durationOptions.map((duration) => ({ value: duration, label: `${duration} min` }))}
				/>
				<SlotFilterSelect
					icon={FiMapPin}
					value={filterLocations}
					placeholder="placówka"
					onChange={setFilterLocations}
					multiple
					options={locationOptions.map((location) => ({
						value: location,
						label: formatLocation(location),
					}))}
				/>
			</Stack>
		);

		return (
			<Box py={4}>
				<Flex justify="space-between" align="center" gap={3}>
					<Heading as="span" size="sm" mx={0} display={{ base: 'none', md: 'inline' }}>
						Filtruj terminy
					</Heading>
					<Button
						display={{ base: 'inline-flex', md: 'none' }}
						onClick={() => setMobileFiltersOpen((open) => !open)}
						fontWeight="bold"
						variant={mobileFiltersOpen || hasAnyFilters ? 'subtle' : 'outline'}
						size="sm"
						justifyContent="start"
						w="full"
						colorPalette='gray'
					>
						<FiFilter />Filtruj<Spacer />{mobileFiltersOpen || hasAnyFilters ? <FiChevronUp /> : <FiChevronDown />}
					</Button>
				</Flex>
				<Flex w="full" alignItems="center" gap={3} display={{ base: 'none', md: 'flex' }}>
					{renderFilterStack()}
					<IconButton
						onClick={clearFilters}
						size="sm"
						aria-label="Wyczyść filtry"
						variant={hasAnyFilters ? 'solid' : 'outline'}
						disabled={!hasAnyFilters}
						flexShrink={0}
					>
						<FaUndo />
					</IconButton>
				</Flex>
				{mobileFiltersOpen && (
					<Box display={{ base: 'block', md: 'none' }} p={3}>
						<Stack gap={3} direction="column" w="full">
							{renderFilterStack(true)}
							<Flex justify="flex-end">
								<IconButton
									onClick={clearFilters}
									size="sm"
									aria-label="Wyczyść filtry"
									variant={hasAnyFilters ? 'solid' : 'outline'}
									disabled={!hasAnyFilters}
								>
									<FaUndo />
								</IconButton>
							</Flex>
						</Stack>
					</Box>
				)}
				<Heading as="span" size="sm" mx={0} pt={3} display={{ base: 'none', md: 'inline' }}>
					Dostępne terminy:
				</Heading>
				{slots.length === 0 && (
					<Flex direction="column" justifyContent="center" h="40px">
						<Text fontStyle="italic" textAlign="center">
							Brak dostępnych terminów spełniających kryteria podane w filtrach.
						</Text>
					</Flex>
				)}
				<Box position="relative" my={3} maxH={300}>
					<Box overflowY="auto" maxH={300} pr={1} pb={2}>
						{slots.map((slot: any, index: number) => (
							<SlotCard
								key={index}
								slot={slot}
								onClick={() => props.onPick(slot)}
							/>
						))}
					</Box>
					{shouldShowScrollHint && (
						<Box
							pointerEvents="none"
							position="absolute"
							left={0}
							right={0}
							bottom={0}
							height={10}
							bg="linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 100%)"
						/>
					)}
				</Box>
				{shouldShowScrollHint && (
					<Text
						mt={-1}
						fontSize="xs"
						color="gray.500"
						textAlign="center"
						fontStyle="italic"
					>
						Przewiń w dół, by zobaczyć wszystkie terminy
					</Text>
				)}
			</Box>
		);
	} else
		return (
			<Flex direction="column" justifyContent="center" mt={6} pb={0} gap={3}>
				<Text fontStyle="italic" textAlign="center" mb={2}>
					Chwilowo brak dostępnych terminów...
				</Text>
				<EnrollmentLeadForm variant="default" />
			</Flex>
		);
};